import axios from 'axios';
import camelize from 'camelize';

import { api, defaultTransformResponse, transformRequest } from '.';
import {
  ICharacterPost,
  ICharacterPostPayload,
  IComment,
  ICommunityPoll,
  ICommunityPost,
  IEFinderObject,
  IPagination,
} from '../model';
import { toQueryString } from '../utils/misc';

export const fetchPost = async (id: number) => {
  const { data } = await api.get<ICommunityPost>(`/communities/${id}/`);
  return data;
};

export const togglePostLike = async (id: number) => {
  const { data } = await api.post<{ isLiked: boolean }>(
    `/communities/${id}/like/`
  );
  return data;
};

export const fetchUserPosts = async (
  userId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<ICommunityPost>>(
    `/users/${userId}/communities/${toQueryString({ page, perPage })}`
  );
  return data;
};

export interface ICommunityPollPayload {
  options: { content: string }[];
  endsAt?: string;
}

export interface ICommunityPayload
  extends Pick<
    ICommunityPost,
    'isFixed' | 'content' | 'isCommentEnabled' | 'isPublicFeedExcluded'
  > {
  pictures?: IEFinderObject<'image'>[];
  poll?: ICommunityPollPayload | null;
}

export const fetchMyPosts = async (page = 1, perPage = 20) => {
  const { data } = await api.get<IPagination<ICommunityPost>>(
    `/users/me/communities/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchMyCommunityPost = async (id: number) => {
  const { data } = await api.get<ICommunityPost>(
    `/users/me/communities/${id}/`
  );
  return data;
};

export const postMyCommunityPost = async (payload: ICommunityPayload) => {
  const { data } = await api.post<ICommunityPost>(
    '/users/me/communities/',
    payload
  );
  return data;
};

export const patchMyCommunityPost = async ({
  id,
  ...payload
}: { id: number } & Partial<ICommunityPayload>) => {
  const { data } = await api.patch<ICommunityPost>(
    `/users/me/communities/${id}/`,
    payload
  );
  return data;
};

export const deleteMyCommunityPost = async (id: number) => {
  const { data } = await api.delete(`/users/me/communities/${id}/`);
  return data;
};

/* 투표 API */

export const voteCommunityPoll = async (
  communityId: number,
  optionId: number
) => {
  const { data } = await api.post<{
    poll: ICommunityPoll;
    myVoteOptionId: number;
  }>(`/communities/${communityId}/poll/votes/`, { optionId });
  return data;
};

/* 캐릭터 커뮤니티 api */

export const fetchCharacterPost = async (id: number) => {
  const { data } = await api.get<ICharacterPost>(`/play/communities/${id}/`);
  return data;
};

export const fetchCharacterPosts = async (
  characterId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<ICharacterPost>>(
    `/play/characters/${characterId}/communities/${toQueryString({
      page,
      perPage,
    })}`
  );
  return data;
};

export const postCharacterPost = async (payload: ICharacterPostPayload) => {
  const { data } = await api.post<ICharacterPost>(
    '/play/my-communities/',
    payload
  );
  return data;
};

export const patchCharacterPost = async ({
  postId,
  ...payload
}: { postId: number } & Partial<ICharacterPostPayload>) => {
  const { data } = await api.patch<ICharacterPost>(
    `/play/my-communities/${postId}/`,
    payload
  );
  return data;
};

export const deleteCharacterPost = async (id: number) => {
  const { data } = await api.delete(`/play/my-communities/${id}/`);
  return data;
};

export const toggleCharacterPostLike = async (id: number) => {
  const { data } = await api.post<{ isLiked: boolean }>(
    `/play/communities/${id}/like/`
  );
  return data;
};

/* 커뮤니티 피드 API */

export type CommunityFeedSort = 'recent' | 'hot' | 'latest';

export type CommunityFeedObjectType = 'user' | 'character';

export interface ICommunityFeedPost extends ICommunityPost {
  objectType: CommunityFeedObjectType;
  objectId: number;
  previewComments?: IComment[];
}

export const fetchCommunityFeed = async (
  type: CommunityFeedSort = 'recent',
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<ICommunityFeedPost>>(
    `/communities/feed/${toQueryString({ page, perPage, type })}`
  );
  return data;
};

const publicApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  responseType: 'json',
  transformRequest,
  transformResponse: [
    ...defaultTransformResponse,
    (data: { result: object }) => camelize(data.result),
  ],
});

export const fetchCommunityFeedPublic = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _type: CommunityFeedSort = 'latest',
  page = 1,
  perPage = 20
) => {
  const { data } = await publicApi.get<IPagination<ICommunityFeedPost>>(
    `/communities/feed/${toQueryString({ page, perPage, type: 'recent' })}`
  );
  return data;
};
